import React, { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { growerService, GrowerCreateData, GrowerUpdateData, GrowerFamilySensor, GrowerFamilyUser } from '../services/growerService';
import { userService, UserWithGrower } from '../services/userService';
import Layout from '../components/Layout';
import {
  Grower, GrowerDetails, GrowerSubItem,
  DeleteSubGrowerRequest, User,
} from '../types';

// ── Form state ───────────────────────────────────────────────────────────────

interface FormData {
  name: string;
  description: string;
  location: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_postal_code: string;
  address_country: string;
  website: string;
  contact_user_id: number | null;
  parent_id: number | null;
}

const emptyFormData: FormData = {
  name: '', description: '', location: '',
  address_street: '', address_city: '', address_state: '',
  address_postal_code: '', address_country: '',
  website: '', contact_user_id: null, parent_id: null,
};

// ── Component ────────────────────────────────────────────────────────────────

const Growers: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [users, setUsers] = useState<UserWithGrower[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // Grower create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingGrower, setEditingGrower] = useState<Grower | null>(null);
  const [creatingSubForId, setCreatingSubForId] = useState<number | null>(null);
  const [growerDetails, setGrowerDetails] = useState<GrowerDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyFormData);
  const [formError, setFormError] = useState('');

  // Sub-grower details panel
  const [selectedSubGrower, setSelectedSubGrower] = useState<GrowerDetails | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [loadingSubDetails, setLoadingSubDetails] = useState(false);

  // Assign sensor modal
  const [showAssignSensorModal, setShowAssignSensorModal] = useState(false);
  const [assignSensorForSubId, setAssignSensorForSubId] = useState<number | null>(null);
  const [parentSensors, setParentSensors] = useState<GrowerFamilySensor[]>([]);
  const [assignSensorError, setAssignSensorError] = useState('');

  // Assign user modal
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [assignUserForSubId, setAssignUserForSubId] = useState<number | null>(null);
  const [parentUsers, setParentUsers] = useState<GrowerFamilyUser[]>([]);
  const [assignUserError, setAssignUserError] = useState('');

  // Delete sub-grower modal
  const [showDeleteSubModal, setShowDeleteSubModal] = useState(false);
  const [deletingSubGrower, setDeletingSubGrower] = useState<GrowerSubItem | null>(null);
  const [deletingParentId, setDeletingParentId] = useState<number | null>(null);
  const [deleteUserAction, setDeleteUserAction] = useState<'parent' | 'unassign' | 'other_subgrower'>('parent');
  const [deleteTargetSubId, setDeleteTargetSubId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      const growersData = await growerService.getGrowers();
      setGrowers(growersData);
      if (currentUser.is_admin) {
        try {
          const usersData = await userService.getUsers();
          setUsers(usersData);
        } catch (err) {
          console.error('Failed to load users:', err);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setPageError('Failed to load growers');
    } finally {
      setLoading(false);
    }
  };

  // Return parent grower IDs this admin grower manages
  const adminGrowerParentIds = (): number[] => {
    if (!user?.is_admin_grower || user.is_admin) return [];
    return growers
      .filter((g) => g.parent_id === null && user.grower_ids.includes(g.id))
      .map((g) => g.id);
  };

  const isAdminGrowerOf = (growerId: number): boolean => {
    const parentIds = adminGrowerParentIds();
    if (parentIds.includes(growerId)) return true;
    const g = growers.find((g) => g.id === growerId);
    return !!(g && g.parent_id !== null && parentIds.includes(g.parent_id));
  };

  const canManage = user?.is_admin || (user?.is_admin_grower ?? false);

  const getSubGrowers = (parentId: number): Grower[] =>
    growers.filter((g) => g.parent_id === parentId);

  // ── Grower modal ──────────────────────────────────────────────────────────

  const openModal = async (grower?: Grower, parentId?: number) => {
    if (grower) {
      setEditingGrower(grower);
      setCreatingSubForId(null);
      setFormData({
        name: grower.name, description: grower.description || '',
        location: grower.location || '', address_street: grower.address_street || '',
        address_city: grower.address_city || '', address_state: grower.address_state || '',
        address_postal_code: grower.address_postal_code || '',
        address_country: grower.address_country || '', website: grower.website || '',
        contact_user_id: grower.contact_user_id, parent_id: grower.parent_id,
      });
      // Load overview for the parent grower
      const detailsId = grower.parent_id ?? grower.id;
      if (canManage) {
        setLoadingDetails(true);
        try {
          setGrowerDetails(await growerService.getGrowerDetails(detailsId));
        } catch (err) {
          console.error('Failed to load grower details:', err);
        } finally {
          setLoadingDetails(false);
        }
      }
    } else {
      setEditingGrower(null);
      setCreatingSubForId(parentId ?? null);
      setGrowerDetails(null);
      setFormData({ ...emptyFormData, parent_id: parentId ?? null });
    }
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGrower(null);
    setCreatingSubForId(null);
    setGrowerDetails(null);
    setFormData(emptyFormData);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const data: GrowerCreateData | GrowerUpdateData = {
      name: formData.name,
      description: formData.description || undefined,
      location: formData.location || undefined,
      address_street: formData.address_street || undefined,
      address_city: formData.address_city || undefined,
      address_state: formData.address_state || undefined,
      address_postal_code: formData.address_postal_code || undefined,
      address_country: formData.address_country || undefined,
      website: formData.website || undefined,
      contact_user_id: formData.contact_user_id,
      parent_id: formData.parent_id,
    };
    try {
      if (editingGrower) {
        await growerService.updateGrower(editingGrower.id, data);
      } else {
        await growerService.createGrower(data as GrowerCreateData);
      }
      closeModal();
      await loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save grower');
    }
  };

  const handleDeleteParentGrower = async (id: number) => {
    if (!window.confirm('Delete this grower and all its sub-growers? This cannot be undone.')) return;
    try {
      await growerService.deleteGrower(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete grower');
    }
  };

  // ── Sub-grower details panel ──────────────────────────────────────────────

  const openSubGrowerDetails = async (parentId: number, subId: number) => {
    setSelectedParentId(parentId);
    setLoadingSubDetails(true);
    try {
      setSelectedSubGrower(await growerService.getGrowerDetails(subId));
    } catch (err) {
      console.error('Failed to load sub-grower details:', err);
    } finally {
      setLoadingSubDetails(false);
    }
  };

  // ── Assign sensor ─────────────────────────────────────────────────────────

  const openAssignSensorModal = async (parentId: number, subId: number) => {
    setAssignSensorForSubId(subId);
    setAssignSensorError('');
    try {
      const family = await growerService.getGrowerFamily(parentId);
      // Show all sensors in the family EXCEPT those already on the target sub-grower
      setParentSensors(family.sensors.filter((s) => s.current_grower_id !== subId));
    } catch (err) {
      console.error('Failed to load family sensors:', err);
      setParentSensors([]);
    }
    setShowAssignSensorModal(true);
  };

  const handleAssignSensor = async (sensorId: number) => {
    if (!assignSensorForSubId) return;
    try {
      await growerService.assignSensorToSubGrower(assignSensorForSubId, sensorId);
      setShowAssignSensorModal(false);
      await loadData();
      if (selectedParentId && selectedSubGrower) {
        await openSubGrowerDetails(selectedParentId, selectedSubGrower.id);
      }
    } catch (err: any) {
      setAssignSensorError(err.response?.data?.detail || 'Failed to assign sensor');
    }
  };

  const handleUnassignSensor = async (subId: number, sensorId: number) => {
    if (!selectedParentId) return;
    try {
      await growerService.unassignSensorFromSubGrower(subId, sensorId);
      await loadData();
      await openSubGrowerDetails(selectedParentId, subId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unassign sensor');
    }
  };

  // ── Assign user ───────────────────────────────────────────────────────────

  const openAssignUserModal = async (parentId: number, subId: number) => {
    setAssignUserForSubId(subId);
    setAssignUserError('');
    try {
      const family = await growerService.getGrowerFamily(parentId);
      // Show all users in the family EXCEPT those already assigned to the target sub-grower
      setParentUsers(family.users.filter((u) => !u.grower_ids.includes(subId)));
    } catch (err) {
      console.error('Failed to load family users:', err);
      setParentUsers([]);
    }
    setShowAssignUserModal(true);
  };

  const handleAssignUser = async (userId: number) => {
    if (!assignUserForSubId) return;
    try {
      await growerService.assignUserToSubGrower(assignUserForSubId, userId);
      setShowAssignUserModal(false);
      await loadData();
      if (selectedParentId && selectedSubGrower) {
        await openSubGrowerDetails(selectedParentId, selectedSubGrower.id);
      }
    } catch (err: any) {
      setAssignUserError(err.response?.data?.detail || 'Failed to assign user');
    }
  };

  const handleUnassignUser = async (subId: number, userId: number) => {
    if (!selectedParentId) return;
    try {
      await growerService.unassignUserFromSubGrower(subId, userId);
      await loadData();
      await openSubGrowerDetails(selectedParentId, subId);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unassign user');
    }
  };

  // ── Delete sub-grower ─────────────────────────────────────────────────────

  const openDeleteSubModal = (parentId: number, sub: GrowerSubItem) => {
    setDeletingSubGrower(sub);
    setDeletingParentId(parentId);
    setDeleteUserAction('parent');
    setDeleteTargetSubId(null);
    setDeleteError('');
    setShowDeleteSubModal(true);
  };

  const handleConfirmDeleteSub = async () => {
    if (!deletingSubGrower || !deletingParentId) return;
    if (deleteUserAction === 'other_subgrower' && !deleteTargetSubId) {
      setDeleteError('Please select a sub-grower to move users to.');
      return;
    }
    const req: DeleteSubGrowerRequest = {
      user_action: deleteUserAction,
      target_subgrower_id: deleteTargetSubId,
    };
    try {
      await growerService.deleteSubGrower(deletingParentId, deletingSubGrower.id, req);
      setShowDeleteSubModal(false);
      if (selectedSubGrower?.id === deletingSubGrower.id) setSelectedSubGrower(null);
      await loadData();
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete sub-grower');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  const parentGrowers = growers.filter((g) => g.parent_id === null);
  const isSystemAdmin = user?.is_admin;

  const renderParentGrowerCard = (grower: Grower) => {
    const subs = getSubGrowers(grower.id);
    const canAdmin = isSystemAdmin || isAdminGrowerOf(grower.id);

    return (
      <div key={grower.id} className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-900">{grower.name}</h3>
            <div className="flex space-x-1 ml-2 flex-shrink-0">
              {canAdmin && (
                <button
                  onClick={() => openModal(grower)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
              )}
              {isSystemAdmin && (
                <button
                  onClick={() => handleDeleteParentGrower(grower.id)}
                  className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          </div>

          {grower.description && <p className="text-sm text-gray-600 mb-1">{grower.description}</p>}
          {grower.location && (
            <p className="text-sm text-gray-500 mb-1">
              <span className="font-medium">Location:</span> {grower.location}
            </p>
          )}
          {(grower.address_city || grower.address_country) && (
            <p className="text-sm text-gray-500 mb-1">
              {[grower.address_city, grower.address_country].filter(Boolean).join(', ')}
            </p>
          )}
          <p className="text-xs text-gray-400 mb-3">
            Created: {new Date(grower.created_at).toLocaleDateString()}
          </p>

          {/* Sub-growers section */}
          {canAdmin && (
            <div className="border-t pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Sub-growers ({subs.length})</span>
                <button
                  onClick={() => openModal(undefined, grower.id)}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                >
                  + Add Sub-grower
                </button>
              </div>
              {subs.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No sub-growers yet.</p>
              ) : (
                <div className="space-y-1">
                  {subs.map((sg) => (
                    <div
                      key={sg.id}
                      className={`flex flex-col p-2 rounded border cursor-pointer transition-colors ${
                        selectedSubGrower?.id === sg.id
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                      onClick={() => openSubGrowerDetails(grower.id, sg.id)}
                    >
                      <span className="text-sm font-medium text-gray-800 break-words mb-1" title={sg.name}>{sg.name}</span>
                      <div className="flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openAssignSensorModal(grower.id, sg.id)}
                          className="px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          title="Assign sensors"
                        >
                          Sensors
                        </button>
                        <button
                          onClick={() => openAssignUserModal(grower.id, sg.id)}
                          className="px-1.5 py-0.5 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600"
                          title="Assign users"
                        >
                          Users
                        </button>
                        <button
                          onClick={() => openModal(growers.find((g) => g.id === sg.id)!)}
                          className="px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteSubModal(grower.id, sg as unknown as GrowerSubItem)}
                          className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Del
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubGrowerPanel = () => {
    if (!selectedSubGrower) return null;
    return (
      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{selectedSubGrower.name} — Details</h3>
          <button
            onClick={() => setSelectedSubGrower(null)}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
        {loadingSubDetails ? (
          <div className="p-6 text-gray-500">Loading...</div>
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sensors */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Assigned Sensors ({selectedSubGrower.sensors.length})</h4>
                {canManage && selectedParentId && (
                  <button
                    onClick={() => openAssignSensorModal(selectedParentId, selectedSubGrower.id)}
                    className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    + Assign
                  </button>
                )}
              </div>
              {selectedSubGrower.sensors.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No sensors assigned.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {selectedSubGrower.sensors.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{s.name}</span>
                        {s.location && <span className="text-gray-400 ml-1 text-xs">({s.location})</span>}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {s.is_active ? 'Active' : 'Off'}
                        </span>
                        {canManage && (
                          <button
                            onClick={() => handleUnassignSensor(selectedSubGrower.id, s.id)}
                            className="text-xs text-red-500 hover:text-red-700 font-bold"
                            title="Return to parent"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Users */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Assigned Users ({selectedSubGrower.users.length})</h4>
                {canManage && selectedParentId && (
                  <button
                    onClick={() => openAssignUserModal(selectedParentId, selectedSubGrower.id)}
                    className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600"
                  >
                    + Assign
                  </button>
                )}
              </div>
              {selectedSubGrower.users.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No users assigned.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {selectedSubGrower.users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{u.full_name || u.email}</span>
                        {u.is_admin_grower && (
                          <span className="ml-1 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Admin</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {u.is_active ? 'Active' : 'Off'}
                        </span>
                        {canManage && (
                          <button
                            onClick={() => handleUnassignUser(selectedSubGrower.id, u.id)}
                            className="text-xs text-red-500 hover:text-red-700 font-bold"
                            title="Return to parent"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main JSX ──────────────────────────────────────────────────────────────

  return (
    <Layout user={user || undefined}>
      <div className="py-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">Growers</h2>
          {isSystemAdmin && (
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Add Grower
            </button>
          )}
        </div>

        {pageError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{pageError}</div>
        )}

        {parentGrowers.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">No growers found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {parentGrowers.map(renderParentGrowerCard)}
          </div>
        )}

        {/* Sub-grower details panel */}
        {renderSubGrowerPanel()}

        {/* ── Grower create/edit modal ──────────────────────────────────── */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-4 sm:mx-auto p-5 border w-auto max-w-4xl shadow-lg rounded-md bg-white mb-10">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingGrower
                  ? `Edit ${editingGrower.parent_id ? 'Sub-grower' : 'Grower'}: ${editingGrower.name}`
                  : creatingSubForId
                  ? `New Sub-grower under "${growers.find((g) => g.id === creatingSubForId)?.name}"`
                  : 'Add Grower'}
              </h3>
              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{formError}</div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input type="text" required value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={formData.description} rows={2}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input type="text" value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input type="text" value={formData.website} placeholder="https://example.com"
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Address</h4>
                    <div className="space-y-2">
                      <input type="text" placeholder="Street" value={formData.address_street}
                        onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" placeholder="City" value={formData.address_city}
                          onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                        <input type="text" placeholder="State/Province" value={formData.address_state}
                          onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" placeholder="Postal Code" value={formData.address_postal_code}
                          onChange={(e) => setFormData({ ...formData, address_postal_code: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                        <input type="text" placeholder="Country" value={formData.address_country}
                          onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                      </div>
                    </div>
                  </div>
                  {/* Parent grower selector (system admin only, top-level growers only) */}
                  {isSystemAdmin && !creatingSubForId && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Parent Grower (optional)</label>
                      <select value={formData.parent_id || ''}
                        onChange={(e) => setFormData({ ...formData, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md">
                        <option value="">-- No parent (top-level) --</option>
                        {growers.filter((g) => g.parent_id === null && (!editingGrower || g.id !== editingGrower.id)).map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Contact person (system admin only) */}
                  {isSystemAdmin && users.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                      <select value={formData.contact_user_id || ''}
                        onChange={(e) => setFormData({ ...formData, contact_user_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md">
                        <option value="">-- Select --</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.email})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex space-x-2 mt-4">
                    <button type="button" onClick={closeModal}
                      className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                      Cancel
                    </button>
                    <button type="submit"
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                      {editingGrower ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>

                {/* Overview panel (when editing) */}
                {editingGrower && (
                  <div className="border-t lg:border-t-0 lg:border-l pt-6 lg:pt-0 lg:pl-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Grower Overview</h4>
                    {loadingDetails ? (
                      <div className="text-gray-500">Loading...</div>
                    ) : growerDetails ? (
                      <div className="space-y-4">
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Sensors ({growerDetails.sensors.length})</h5>
                          {growerDetails.sensors.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">None</p>
                          ) : (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {growerDetails.sensors.map((s) => (
                                <div key={s.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                                  <span className="font-medium">{s.name}</span>
                                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {s.is_active ? 'Active' : 'Off'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Users ({growerDetails.users.length})</h5>
                          {growerDetails.users.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">None</p>
                          ) : (
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {growerDetails.users.map((u) => (
                                <div key={u.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                                  <span className="font-medium">{u.full_name || u.email}</span>
                                  <div className="flex items-center space-x-1">
                                    {u.is_admin_grower && (
                                      <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Admin</span>
                                    )}
                                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                      {u.is_active ? 'Active' : 'Off'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {growerDetails.sub_growers.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Sub-growers ({growerDetails.sub_growers.length})</h5>
                            <div className="space-y-1">
                              {growerDetails.sub_growers.map((sg) => (
                                <div key={sg.id} className="p-2 bg-gray-50 rounded text-sm font-medium">{sg.name}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Details not available</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Assign Sensor Modal ───────────────────────────────────────── */}
        {showAssignSensorModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-4 sm:mx-auto p-5 border w-auto max-w-md shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Sensor to Sub-grower</h3>
              {assignSensorError && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{assignSensorError}</div>
              )}
              <p className="text-sm text-gray-600 mb-3">
                Select a sensor to move to this sub-grower. Sensors from the parent grower and
                sibling sub-growers are all shown.
              </p>
              {parentSensors.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No sensors available to assign.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {parentSensors.map((s) => (
                    <button key={s.id} onClick={() => handleAssignSensor(s.id)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-yellow-50 border border-gray-200 hover:border-yellow-300 text-left">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{s.name}</span>
                        {s.location && <span className="text-xs text-gray-500 ml-2">({s.location})</span>}
                        <span className="text-xs text-gray-400 block">{s.device_id}</span>
                        <span className="text-xs text-blue-600">Currently: {s.current_grower_name}</span>
                      </div>
                      <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full flex-shrink-0 ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.is_active ? 'Active' : 'Off'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => setShowAssignSensorModal(false)}
                className="mt-4 w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Assign User Modal ─────────────────────────────────────────── */}
        {showAssignUserModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-4 sm:mx-auto p-5 border w-auto max-w-md shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assign User to Sub-grower</h3>
              {assignUserError && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{assignUserError}</div>
              )}
              <p className="text-sm text-gray-600 mb-3">
                Select a user to assign to this sub-grower. Users from the parent grower and
                sibling sub-growers are shown. They will gain access to this sub-grower's sensors.
              </p>
              {parentUsers.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No users available to assign.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {parentUsers.map((u) => (
                    <button key={u.id} onClick={() => handleAssignUser(u.id)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 text-left">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{u.full_name || u.email}</span>
                        {u.full_name && <span className="text-xs text-gray-500 block">{u.email}</span>}
                        {u.is_admin_grower && (
                          <span className="text-xs bg-green-100 text-green-700 px-1 rounded">Grower Admin</span>
                        )}
                        <span className="text-xs text-blue-600 block">
                          Currently: {u.grower_names.join(', ') || '—'}
                        </span>
                      </div>
                      <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full flex-shrink-0 ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {u.is_active ? 'Active' : 'Off'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => setShowAssignUserModal(false)}
                className="mt-4 w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Delete Sub-grower Modal ───────────────────────────────────── */}
        {showDeleteSubModal && deletingSubGrower && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-4 sm:mx-auto p-5 border w-auto max-w-lg shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Sub-grower: {deletingSubGrower.name}</h3>
              <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm">
                <strong>Warning:</strong> Deleting this sub-grower will affect its users.
                Sensors will be moved back to the parent grower. Please choose what happens to the affected users:
              </div>
              {deleteError && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{deleteError}</div>
              )}
              <div className="space-y-3 mb-4">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="userAction" value="parent"
                    checked={deleteUserAction === 'parent'}
                    onChange={() => setDeleteUserAction('parent')} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Move users to parent grower</span>
                    <p className="text-xs text-gray-500">Users are reassigned to the parent grower and can view all its sensors.</p>
                  </div>
                </label>
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input type="radio" name="userAction" value="unassign"
                    checked={deleteUserAction === 'unassign'}
                    onChange={() => setDeleteUserAction('unassign')} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Unassign users (no grower)</span>
                    <p className="text-xs text-gray-500">
                      Users will have no grower assignment and cannot view any sensor data.
                      They will be prompted to contact the grower admin on login.
                    </p>
                  </div>
                </label>
                {deletingParentId && getSubGrowers(deletingParentId).filter((sg) => sg.id !== deletingSubGrower.id).length > 0 && (
                  <label className="flex items-start space-x-2 cursor-pointer">
                    <input type="radio" name="userAction" value="other_subgrower"
                      checked={deleteUserAction === 'other_subgrower'}
                      onChange={() => setDeleteUserAction('other_subgrower')} className="mt-0.5" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-800">Move users to another sub-grower</span>
                      {deleteUserAction === 'other_subgrower' && deletingParentId && (
                        <select value={deleteTargetSubId || ''}
                          onChange={(e) => setDeleteTargetSubId(e.target.value ? parseInt(e.target.value) : null)}
                          className="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-sm">
                          <option value="">-- Select sub-grower --</option>
                          {getSubGrowers(deletingParentId)
                            .filter((sg) => sg.id !== deletingSubGrower.id)
                            .map((sg) => (
                              <option key={sg.id} value={sg.id}>{sg.name}</option>
                            ))}
                        </select>
                      )}
                    </div>
                  </label>
                )}
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setShowDeleteSubModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                  Cancel
                </button>
                <button onClick={handleConfirmDeleteSub}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                  Delete Sub-grower
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Growers;
